import { ReactNode, useEffect, useRef } from 'react'
import { ICellRendererParams } from 'ag-grid-community'
import { Box, IconButton } from '@mui/material'
import { DevicesDataItem, IntegrationInstance } from 'models'
import { generateRowUrl } from 'lib/generateRowUrl'
import theme from 'theme'
import OnyxiaTooltip from 'ui/components/tooltip/OnyxiaTooltip'
import { NO_EDIT_PERMISSION_MESSAGE } from 'ui/components/modal/variants/exclusion-rule/exclusionRule.constants'

const LinkIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.25 5H4.375C3.33947 5 2.5 5.83947 2.5 6.875V15.625C2.5 16.6605 3.33947 17.5 4.375 17.5H13.125C14.1605 17.5 15 16.6605 15 15.625V8.75M6.25 13.75L17.5 2.5M17.5 6.875V2.5L13.125 2.5"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ExcludeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.5 10H7.5M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10Z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const TicketIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18.3388 7.39656C18.283 6.11156 18.1271 5.2749 17.6888 4.61323C17.4395 4.23624 17.1268 3.90524 16.7646 3.6349C15.793 2.91406 14.4221 2.91406 11.6788 2.91406H8.33297C5.59047 2.91406 4.21881 2.91406 3.24714 3.6349C2.88714 3.90156 2.57464 4.2324 2.32214 4.61323C1.88464 5.2749 1.72881 6.11156 1.67214 7.39656C1.66381 7.61573 1.85214 7.78406 2.05964 7.78406C3.21464 7.78406 4.15131 8.7749 4.15131 9.9974C4.15131 11.2199 3.21464 12.2107 2.05964 12.2107C1.85214 12.2107 1.66297 12.3791 1.67297 12.5991C1.72881 13.8832 1.88464 14.7199 2.32297 15.3824C2.57235 15.7591 2.88502 16.0898 3.24714 16.3599C4.21881 17.0807 5.59047 17.0807 8.33297 17.0807H11.6788C14.4213 17.0807 15.793 17.0807 16.7646 16.3599C17.1246 16.0932 17.4371 15.7624 17.6896 15.3816C18.1271 14.7199 18.283 13.8832 18.3388 12.5982V7.39656Z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <path
      d="M10.6719 7.08594V9.79427M10.6719 12.5026V9.79427M10.6719 9.79427H13.1719M10.6719 9.79427H8.17188"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
)

interface ActionsCellRendererProps extends ICellRendererParams {
  dataSourceOptions: IntegrationInstance[]
  onExclude?: (row: DevicesDataItem) => void
  canExclude?: boolean
  onCreateTicket?: (row: DevicesDataItem) => void
  /* Read on every render, so a refreshCells picks up integrations that loaded after the grid. */
  getTicketBlockedReason?: () => ReactNode
}

const ActionsCellRenderer = (props: ActionsCellRendererProps) => {
  const { data, dataSourceOptions, eGridCell, onExclude, canExclude = true, onCreateTicket, getTicketBlockedReason } =
    props
  const ticketBlockedReason = getTicketBlockedReason?.() ?? null
  const hasAddedClass = useRef(false)

  const url = data ? generateRowUrl(data as DevicesDataItem, dataSourceOptions) : null

  useEffect(() => {
    if (!data || !eGridCell || hasAddedClass.current) return

    if (url) {
      const pinnedRightRow = eGridCell.closest('.ag-row')
      if (pinnedRightRow) {
        pinnedRightRow.classList.add('has-action-link')

        const rowIndexAttr = pinnedRightRow.getAttribute('row-index')

        const bodyViewport = eGridCell.closest('.ag-body-viewport')
        if (bodyViewport && rowIndexAttr) {
          const centerRow = bodyViewport.querySelector(`.ag-center-cols-container .ag-row[row-index="${rowIndexAttr}"]`)

          if (centerRow) {
            centerRow.classList.add('main-row-with-action-link')
            hasAddedClass.current = true
          }
        }
      }
    }
  }, [data, dataSourceOptions, eGridCell, url])

  if (!data) return null

  const handleLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleExcludeClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onExclude?.(data as DevicesDataItem)
  }

  const linkButtonSx = {
    color: theme.colors.menuItemText,
    '&:hover': {
      svg: {
        path: { stroke: theme.colors.devicesSecondaryText }
      }
    }
  }

  const excludeButtonSx = {
    color: theme.colors.menuItemText,
    '&:hover': {
      svg: {
        path: { stroke: theme.colors.cta }
      }
    },
    // MUI's disabled colour is near-black, which vanishes on the dark rows.
    '&.Mui-disabled': {
      color: theme.colors.menuItemText,
      opacity: 0.4
    }
  }

  const excludeTooltipProps = {
    tooltip: {
      sx: {
        width: canExclude ? '239px' : '280px',
        minHeight: '41px',
        boxSizing: 'border-box',
        padding: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        whiteSpace: canExclude ? 'nowrap' : 'normal',
        textAlign: 'left'
      }
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        height: '100%'
      }}
    >
      {url && (
        <IconButton size="small" onClick={handleLinkClick} sx={linkButtonSx}>
          <LinkIcon />
        </IconButton>
      )}
      {onCreateTicket && (
        <OnyxiaTooltip
          title={ticketBlockedReason ?? 'Create Ticket'}
          placement="bottom-end"
          arrow={false}
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: '280px',
                minHeight: '41px',
                boxSizing: 'border-box',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                whiteSpace: ticketBlockedReason ? 'normal' : 'nowrap'
              }
            }
          }}
        >
          <span>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                onCreateTicket(data as DevicesDataItem)
              }}
              sx={excludeButtonSx}
              disabled={Boolean(ticketBlockedReason)}
              aria-label="Create ticket"
            >
              <TicketIcon />
            </IconButton>
          </span>
        </OnyxiaTooltip>
      )}
      {onExclude && (
        <OnyxiaTooltip
          title={canExclude ? 'Exclude by Creating an Exclusion Rule' : NO_EDIT_PERMISSION_MESSAGE}
          placement="bottom-end"
          arrow={false}
          componentsProps={excludeTooltipProps}
        >
          <span>
            <IconButton
              size="small"
              onClick={handleExcludeClick}
              sx={excludeButtonSx}
              disabled={!canExclude}
              aria-label="Exclude entity"
            >
              <ExcludeIcon />
            </IconButton>
          </span>
        </OnyxiaTooltip>
      )}
    </Box>
  )
}

export default ActionsCellRenderer
